import { Box, Typography } from '@material-ui/core'
import UpdateIcon from '@material-ui/icons/Update'
import { useEffect } from 'react'
import {
  ArrayField,
  Button,
  Datagrid,
  DateField,
  FunctionField,
  Loading,
  NumberField,
  ReferenceField,
  ShowController,
  ShowView,
  Tab,
  TabbedShowLayout,
  TextField,
  useDataProvider,
  useRedirect,
  useRefresh,
} from 'react-admin'
import { JsonField } from 'react-admin-json-view'

import Aside from '../Aside/aside'
import {
  ConditionalGreyedTextField,
  CustomBooleanField,
  DateWindowField,
  HoursDetailsField,
  LocalizedStopDateTimeField,
  RevenueDetailsField,
  StatusTextField,
} from '../Fields'
import { UserLink } from '../User/UserLink'
import ScheduleCalendar from './ScheduleCalendar'
import ScheduleCalendarWarning from './ScheduleCalendarWarning'
import { ScheduleRecordtoForm } from './ScheduleGenerate'

const RegenerateScheduleButton = ({ record, redirectTo }: any) => {
  const initialValues = ScheduleRecordtoForm(record)

  const regenerate_schedule = () => {
    redirectTo('/schedules/generate', '', null, {}, { initial_values: initialValues })
  }

  return (
    <Button label="Regenerate schedule with same parameters" onClick={regenerate_schedule}>
      <UpdateIcon />
    </Button>
  )
}

const ScheduleShow = (props: any) => {
  const { id, resource } = props

  const dataProvider = useDataProvider()
  const refresh = useRefresh()
  const redirectTo = useRedirect()

  // Refresh the page every [delay] seconds while status is PENDING
  useEffect(() => {
    const delay = 10000 // 10 seconds
    let timer: any

    // https://stackoverflow.com/a/61407381
    function tick() {
      dataProvider.getOne(resource, { id: id }).then(({ data }) => {
        if (data['status'] === 'PENDING') {
          timer = setTimeout(tick, delay)
          refresh()
        }
      })
    }

    timer = setTimeout(tick, delay)
    return () => clearTimeout(timer)
  }, [dataProvider, id, refresh, resource]) // restart timer if any of these dependencies change, for example, when clicking the "regenerate" button

  return (
    <ShowController {...props}>
      {(controllerProps) => (
        <ShowView
          {...props}
          {...controllerProps}
          aside={<Aside record={controllerProps.record} resource="schedules" />}
        >
          <TabbedShowLayout>
            <Tab label="Schedule">
              <RegenerateScheduleButton redirectTo={redirectTo} />
              <br></br>
              <StatusTextField source="status" />
              <TextField source="label" />
              <TextField source="id" />
              <TextField source="group_id" />
              <FunctionField label="Driver" render={(record: any) => <UserLink user={record.driver} />} />
              <FunctionField
                label="Created by"
                render={(record: any) => <UserLink user={record.created_by} />}
              />
              <DateField source="date_created" showTime={true} options={{ timeZoneName: 'short' }} />
              <DateWindowField />
              <RevenueDetailsField />
              <HoursDetailsField />
              <NumberField source="truck_weight_lb__max" label="Max load weight (lb)" />
              <CustomBooleanField source="share_with_driver" label="Shared with driver?" />
              <ArrayField source="events">
                <Datagrid>
                  <ReferenceField source="load.id" reference="loads" label="Load" link="show">
                    <TextField source="id" />
                  </ReferenceField>
                  <ReferenceField source="job.id" reference="jobs" label="Job*" link="show">
                    <TextField source="human_id" />
                  </ReferenceField>
                  {
                    <ConditionalGreyedTextField
                      source="type"
                      label="Type**"
                      // @ts-ignore
                      condition={(record: any) => record.source !== 'OPTIMIZER'}
                    />
                  }
                  <TextField source="load_stop.type" label="Stop Type" />
                  <TextField source="location" />
                  <LocalizedStopDateTimeField source="load_stop.date_start" label="Load Stop Date Start" />
                  <NumberField source="drive_mi" />
                  <NumberField source="truck_weight_lb" label="Load weight (lb)" />
                  <HoursDetailsField />
                  <RevenueDetailsField />
                </Datagrid>
              </ArrayField>
              <Typography variant="caption">* Reference to driver's existing job in schedule.</Typography>
              <Typography variant="caption">
                ** A greyed out Type indicates the event was externally supplied to optimize around.
              </Typography>
              {controllerProps.record && controllerProps.record.status === 'PENDING' && (
                <Loading
                  loadingPrimary="Schedule generation in progress"
                  loadingSecondary="Status will change to READY or ERROR when finished"
                />
              )}
            </Tab>
            <Tab label="Feasibility" path="feasibility">
              <TextField source="start_location" />
              <DateWindowField label="Start & End Dates" />
              <JsonField
                source="hos_clocks"
                addLabel={true}
                // @ts-ignore
                reactJsonOptions={{
                  name: 'Initial Clocks',
                  collapsed: true,
                  enableClipboard: false,
                  displayDataTypes: false,
                }}
              />
              <ArrayField source="events">
                <Datagrid>
                  <ReferenceField source="load.id" reference="loads" label="Load" link="show">
                    <TextField source="source_human_id" />
                  </ReferenceField>
                  <ReferenceField source="job.id" reference="jobs" label="Job" link="show">
                    <TextField source="human_id" />
                  </ReferenceField>
                  <TextField source="load_stop.type" label="Stop Type" />
                  <TextField source="location" />
                  <DateWindowField label="Event Window" />
                  <LocalizedStopDateTimeField source="drive_date_end" label="ETA" />
                  <LocalizedStopDateTimeField source="load_stop.date_start" label="Stop Appt Time Start" />
                  <LocalizedStopDateTimeField source="load_stop.date_end" label="Stop Appt Time End" />
                  <HoursDetailsField />
                  <JsonField
                    source="hos_clocks"
                    // @ts-ignore
                    reactJsonOptions={{
                      name: 'Clocks',
                      collapsed: true,
                      enableClipboard: false,
                      displayDataTypes: false,
                    }}
                  />
                </Datagrid>
              </ArrayField>
              <ArrayField source="flags">
                <Datagrid>
                  <ReferenceField source="load.id" reference="loads" label="Load" link="show">
                    <TextField source="source_human_id" />
                  </ReferenceField>
                  <TextField source="load_stop.type" label="Stop Type" />
                  <ReferenceField source="job.id" reference="jobs" label="Job*" link="show">
                    <TextField source="human_id" />
                  </ReferenceField>
                  <LocalizedStopDateTimeField source="load_stop.date_start" label="Load Stop Date Start" />
                  <TextField source="flag" label="Flag Type" />
                  <TextField source="severity" />
                  <TextField source="message" />
                </Datagrid>
              </ArrayField>
            </Tab>
            <Tab label="Calendar View" path="calendar">
              <Typography variant="caption">
                The top chart represents the **driving plan**, and identifies time spent in each driving
                state.
              </Typography>
              <Typography variant="caption">
                The bottom chart represents the **schedule**. Each colored area corresponds to a load (source
                + source_id in top left).
              </Typography>
              <Box sx={{ ml: 2 }}>
                <Typography variant="caption">
                  A solid line is time spent en-route to a stop (including off-duty time if the driver arrived
                  early).
                </Typography>
              </Box>
              <Box sx={{ ml: 2 }}>
                <Typography variant="caption">A solid rectangle is dwell time at a stop.</Typography>
              </Box>
              <Box sx={{ ml: 2 }}>
                <Typography variant="caption">
                  A transparent rectangle is the actual appointment times (defaulting to an appointment window
                  of 2h if stop end time is unknown).
                </Typography>
              </Box>
              <ScheduleCalendarWarning />
              <ScheduleCalendar />
            </Tab>
            <Tab label="Metadata" path="metadata">
              <JsonField
                source="metadata"
                // @ts-ignore
                reactJsonOptions={{
                  name: 'Metadata',
                  collapsed: false,
                  enableClipboard: false,
                  displayDataTypes: false,
                }}
              />
            </Tab>
          </TabbedShowLayout>
        </ShowView>
      )}
    </ShowController>
  )
}

export default ScheduleShow
